import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface UserPresence {
  oderId: string;
  firstName: string;
  lastName: string;
  avatarUrl?: string;
  areaId?: string;
  areaName?: string;
  streetId?: string;
  streetName?: string;
  houseId?: string;
  houseNumber?: string;
  lastSeen: string;
}

interface UseAreaPresenceProps {
  userId?: string;
  currentAreaId?: string | null;
  currentAreaName?: string;
  currentStreetId?: string | null;
  currentStreetName?: string;
  currentHouseId?: string | null;
  currentHouseNumber?: string;
}

export const useAreaPresence = ({
  userId,
  currentAreaId,
  currentAreaName,
  currentStreetId,
  currentStreetName,
  currentHouseId,
  currentHouseNumber,
}: UseAreaPresenceProps) => {
  const [otherUsers, setOtherUsers] = useState<UserPresence[]>([]);
  const [profile, setProfile] = useState<{ firstName: string; lastName: string; avatarUrl?: string } | null>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  // Fetch user profile
  useEffect(() => {
    if (!userId) return;

    const fetchProfile = async () => {
      const { data } = await supabase
        .from('profiles')
        .select('first_name, last_name, avatar_url')
        .eq('user_id', userId)
        .single();
      
      if (data) {
        setProfile({
          firstName: data.first_name || 'Unbekannt',
          lastName: data.last_name || '',
          avatarUrl: data.avatar_url || undefined,
        });
      }
    };

    fetchProfile();
  }, [userId]);

  useEffect(() => {
    if (!userId || !profile) return;

    // Clean up previous channel
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
    }

    const channel = supabase.channel('d2d-area-presence', {
      config: {
        presence: {
          key: userId,
        },
      },
    });

    channel
      .on('presence', { event: 'sync' }, () => {
        const presenceState = channel.presenceState();
        const users: UserPresence[] = [];
        
        Object.entries(presenceState).forEach(([key, presences]) => {
          if (key !== userId && presences.length > 0) {
            const presence = presences[0] as any;
            users.push({
              oderId: presence.oderId || key,
              firstName: presence.firstName || 'Unbekannt',
              lastName: presence.lastName || '',
              avatarUrl: presence.avatarUrl,
              areaId: presence.areaId,
              areaName: presence.areaName,
              streetId: presence.streetId,
              streetName: presence.streetName,
              houseId: presence.houseId,
              houseNumber: presence.houseNumber,
              lastSeen: new Date().toISOString(),
            });
          }
        });
        
        setOtherUsers(users);
      })
      .on('presence', { event: 'join' }, ({ key, newPresences }) => {
        console.log('👤 User joined:', key, newPresences);
      })
      .on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
        console.log('👤 User left:', key);
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          console.log('✅ Presence channel subscribed');
          
          // Track our presence
          await channel.track({
            oderId: userId,
            firstName: profile.firstName,
            lastName: profile.lastName,
            avatarUrl: profile.avatarUrl,
            areaId: currentAreaId,
            areaName: currentAreaName,
            streetId: currentStreetId,
            streetName: currentStreetName,
            houseId: currentHouseId,
            houseNumber: currentHouseNumber,
          });
        }
      });

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        console.log('🔌 Cleaning up presence channel');
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [userId, profile]);

  // Update presence when location changes
  useEffect(() => {
    if (!channelRef.current || !userId || !profile) return;

    const updatePresence = async () => {
      try {
        await channelRef.current?.track({
          oderId: userId,
          firstName: profile.firstName,
          lastName: profile.lastName,
          avatarUrl: profile.avatarUrl,
          areaId: currentAreaId,
          areaName: currentAreaName,
          streetId: currentStreetId,
          streetName: currentStreetName,
          houseId: currentHouseId,
          houseNumber: currentHouseNumber,
        });
      } catch (error) {
        console.error('Error updating presence:', error);
      }
    };

    updatePresence();
  }, [currentAreaId, currentStreetId, currentHouseId, profile, currentAreaName, currentStreetName, currentHouseNumber]);

  // Get users in same area
  const getUsersInArea = useCallback((areaId: string) => {
    return otherUsers.filter(u => u.areaId === areaId);
  }, [otherUsers]);

  // Get users in same street
  const getUsersInStreet = useCallback((streetId: string) => {
    return otherUsers.filter(u => u.streetId === streetId);
  }, [otherUsers]);

  // Get users in same house
  const getUsersInHouse = useCallback((houseId: string) => {
    return otherUsers.filter(u => u.houseId === houseId);
  }, [otherUsers]);

  return {
    otherUsers,
    getUsersInArea,
    getUsersInStreet,
    getUsersInHouse,
  };
};
